import { useFormField } from '@calendly/form';
import { FieldError } from '@calendly/form/parts';
import { HtmlParser } from '@calendly/html-parser';
import { I18n } from '@calendly/i18n';
import { ComponentGroup, useComponentGroupChild } from '@calendly/ui/components/component-group';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { TextInput } from '@calendly/ui/components/text-input';
import { BodyText, LabelText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { useState } from 'react';
import { RESERVED_SLUGS, SLUG_REGEX } from 'components/event_type_edit/event_link/get_validation_schema';
import { useEventTypeContext } from 'features/event_type_provider';
const fieldGroupCss = "f94so6v";
const urlPreviewRowCss = "u8m00vo";
const slugPreviewCss = "s1fg2rzk";
const slugPlaceholderCss = "s11po7k0";
const editButtonCss = "e18ezein";
const editSlugButtonCss = "e156pmuk";
const slugFieldGroupCss = "sv6imby";
const urlPrefixCellCss = "u19xwatk";
const urlPrefixCellInvalidCss = "u1vzjqyp";
const urlFieldGroupCss = "u1dcx46d";
const slugInputCss = "s1mj6v1b";
const slugInputInvalidCss = "s1tkk57z";
const deriveSlugFromName = name => {
  if (!name) return '';
  return name.toLowerCase().replace(/[^a-z0-9\s-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
};
const LiveUrlPreview = ({
  profileSlug,
  nameValue
}) => {
  const prefix = profileSlug ? `calendly.com/${profileSlug}/` : 'calendly.com/';
  const derivedSlug = deriveSlugFromName(nameValue);
  return <div className={urlPreviewRowCss}>
      <BodyText color="muted" size="small" inline>
        {prefix}
      </BodyText>
      {derivedSlug ? <span className={slugPreviewCss}>
          <BodyText size="small" inline>{derivedSlug}</BodyText>
        </span> : <span className={slugPlaceholderCss}>
          <BodyText size="small" inline>your-event</BodyText>
        </span>}
    </div>;
};
const CommittedUrlPreview = ({
  profileSlug,
  slugValue,
  onEditClick
}) => {
  const prefix = profileSlug ? `calendly.com/${profileSlug}/` : 'calendly.com/';
  return <div className={urlPreviewRowCss}>
      <BodyText color="muted" size="small" inline>
        {`${prefix}${slugValue}`}
      </BodyText>
      <MinimalButton type="button" size="small" onClick={onEditClick} data-testid="edit-slug-button" className={editSlugButtonCss}>
        <span className={editButtonCss}>
          <BodyText size="small" inline>
            {I18n.t('publisher.action.edit')}
          </BodyText>
        </span>
      </MinimalButton>
    </div>;
};

// TODO(cui-drift): replace with CUI input-prefix addon once available
const UrlPrefixCell = ({
  prefix,
  invalid
}) => {
  useComponentGroupChild();
  return <span className={classNames(urlPrefixCellCss, {
    [urlPrefixCellInvalidCss]: invalid
  })} aria-hidden="true" data-testid="url-prefix">
      <BodyText color="muted" inline>{prefix}</BodyText>
    </span>;
};
const SlugEditField = ({
  profileSlug,
  slugValue,
  slugError,
  onSlugChange,
  onBlur
}) => {
  const prefix = profileSlug ? `calendly.com/${profileSlug}/` : 'calendly.com/';
  const hasError = Boolean(slugError);
  return <div className={slugFieldGroupCss}>
      <LabelText as="label" htmlFor="slug-field" size="large">
        {I18n.t('publisher.event_type_editor_crud.meeting_details.booking_page_url_label')}
        {' *'}
      </LabelText>
      <ComponentGroup>
        <div className={urlFieldGroupCss}>
          <UrlPrefixCell prefix={prefix} invalid={hasError} />
          <TextInput id="slug-field" name="slug" value={slugValue || ''} onChange={onSlugChange} onBlur={onBlur} aria-invalid={hasError} aria-label={`${I18n.t('publisher.event_type_editor_crud.meeting_details.slug_field_label')}: ${prefix}`} data-testid="slug-input" className={classNames(slugInputCss, {
          [slugInputInvalidCss]: hasError
        })} />
        </div>
      </ComponentGroup>
      {slugError ? <FieldError text={slugError} /> : <BodyText color="muted" size="small" data-testid="slug-note">
          {HtmlParser(I18n.t('publisher.event_type_editor_crud.meeting_details.booking_page_url_note'))}
        </BodyText>}
    </div>;
};
const renderUrlSection = ({
  isCreation,
  isEditingSlug,
  profileSlug,
  nameValue,
  slugValue,
  slugError,
  onSlugChange,
  onSlugBlur,
  onEditClick
}) => {
  if (isCreation) {
    return <LiveUrlPreview profileSlug={profileSlug} nameValue={nameValue} />;
  }
  if (isEditingSlug) {
    return <SlugEditField profileSlug={profileSlug} slugValue={slugValue} slugError={slugError} onSlugChange={onSlugChange} onBlur={onSlugBlur} />;
  }
  return <CommittedUrlPreview profileSlug={profileSlug} slugValue={slugValue} onEditClick={onEditClick} />;
};
export const MeetingNameField = ({
  isLocked
}) => {
  const [{
    value: nameValue
  }, nameMeta, {
    setValue: setNameValue,
    setTouched: setNameTouched,
    setError: setNameError
  }] = useFormField({
    name: 'name'
  });
  const [{
    value: slugValue
  }, slugMeta, {
    setValue: setSlugValue,
    setTouched: setSlugTouched,
    setError: setSlugError
  }] = useFormField({
    name: 'slug'
  });
  const [isEditingSlug, setIsEditingSlug] = useState(false);
  const startEditingSlug = () => setIsEditingSlug(true);
  const {
    eventType,
    isCreation
  } = useEventTypeContext();
  const profileSlug = eventType.profile?.slug;
  const nameError = nameMeta.touched && nameMeta.error ? nameMeta.error : null;
  const slugError = slugMeta.touched && slugMeta.error ? slugMeta.error : null;

  // MeetingNameField renders outside the CRUD tab's panel/accordion system, so neither
  // Formik's schema-driven validation (validateOnBlur/Change are off) nor the tab's
  // ActiveTabValidation (scoped to panelKeys) ever populate an error for these fields.
  // Validate required-ness here directly via the field's own setError helper.
  const handleNameChange = e => {
    setNameValue(e.target.value);
    if (e.target.value?.trim()) setNameError(undefined);
  };
  const handleSlugChange = e => {
    setSlugValue(e.target.value);
    if (e.target.value?.trim()) setSlugError(undefined);
  };
  const handleNameBlur = () => {
    setNameTouched(true);
    setNameError(nameValue?.trim() ? undefined : I18n.t('publisher.event_type_editor_crud.meeting_details.meeting_name_required_error'));
  };
  const handleSlugBlur = () => {
    setSlugTouched(true);
    const trimmedSlug = slugValue?.trim();
    if (!trimmedSlug) {
      setSlugError(I18n.t('publisher.event_type_editor_crud.meeting_details.url_required_error'));
    } else if (!SLUG_REGEX.test(trimmedSlug)) {
      setSlugError(I18n.t('activerecord.errors.models.event_type_base.attributes.slug.invalid'));
    } else if (RESERVED_SLUGS.includes(trimmedSlug)) {
      setSlugError(I18n.t('activerecord.errors.models.event_type_base.attributes.slug.exclusion'));
    } else {
      setSlugError(undefined);
    }
  };
  return <div className={fieldGroupCss} data-testid="meeting-name-field">
      <LabelText as="label" htmlFor="event-name-field" size="large">
        {I18n.t('publisher.event_type_editor_crud.meeting_details.meeting_name_label')}
        {' *'}
      </LabelText>
      <TextInput id="event-name-field" name="name" value={nameValue || ''} onChange={handleNameChange} onBlur={handleNameBlur} placeholder={I18n.t('publisher.event_type_editor.sections.quickstart.name_your_event_label')} maxLength={56} disabled={isLocked} aria-required="true" aria-invalid={Boolean(nameError)} data-testid="meeting-name-input" data-1p-ignore="true" data-lpignore="true" />
      {nameError && <FieldError text={nameError} />}

      {renderUrlSection({
      isCreation,
      isEditingSlug,
      profileSlug,
      nameValue,
      slugValue,
      slugError,
      onSlugChange: handleSlugChange,
      onSlugBlur: handleSlugBlur,
      onEditClick: startEditingSlug
    })}
    </div>;
};

require("./meeting_name_field.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./meeting_name_field.js");